import { useMemo } from 'react';
import { useEntitlement } from '@features/entitlements';
import { useGetOrganizationMembersQuery } from '@features/organizations';
import { useBundleCatalog } from '@features/subscriptions';
import { useUserId } from '@features/user';

interface TelehealthAccess {
  /** Whether the resolved provider has telehealth access (entitlement exists). False while loading. */
  hasAccess: boolean;
  /** Whether the entitlement data is still loading */
  isLoading: boolean;
  /** Whether access was resolved for another provider (on-behalf scheduling) rather than the current user. */
  isOnBehalf: boolean;
}

/**
 * Resolves telehealth (JotMeet) access.
 *
 * With no argument, checks the current user's own `telehealth` entitlement.
 * When `providerId` names a different provider (org-schedule / on-behalf
 * flows), the gate keys off that provider's access instead (GDN-2967): their
 * active bundle assignments from the org members list crossed with the bundle
 * catalog's capability grants. Entitlements are bundle-derived, so a provider
 * has telehealth access iff one of their active bundles grants it — the same
 * grants-based resolution used by `useJotMeetAvailability` and the member
 * plan management flow.
 */
export function useTelehealthAccess(providerId?: string): TelehealthAccess {
  const currentUserId = useUserId();
  const isOnBehalf = Boolean(providerId) && providerId !== currentUserId;

  const { entitlement, isLoading: isOwnLoading } = useEntitlement('telehealth', {
    skip: isOnBehalf,
  });

  // Same query key as the org-schedule ProviderPicker, so this reads from the
  // already-warm members cache in the on-behalf flow.
  const { data: membersData, isLoading: isMembersLoading } = useGetOrganizationMembersQuery(
    { role: 'clinician' },
    { skip: !isOnBehalf },
  );
  const { allBundles, isLoading: isCatalogLoading } = useBundleCatalog({ skip: !isOnBehalf });

  const providerHasAccess = useMemo(() => {
    if (!isOnBehalf) return false;
    const member = membersData?.members?.find(m => m.user_id === providerId);
    if (!member?.bundles?.length) return false;
    const grantingBundleIds = new Set(
      allBundles
        .filter(bundle => bundle.grants?.some(grant => grant.capability_name === 'telehealth'))
        .map(bundle => bundle.bundle_id),
    );
    return member.bundles.some(
      bundle => bundle.status === 'active' && grantingBundleIds.has(bundle.bundle_id),
    );
  }, [isOnBehalf, membersData, providerId, allBundles]);

  if (isOnBehalf) {
    // Still loading — default to hidden to prevent flash.
    if (isMembersLoading || isCatalogLoading) {
      return { hasAccess: false, isLoading: true, isOnBehalf };
    }
    return { hasAccess: providerHasAccess, isLoading: false, isOnBehalf };
  }

  // Still loading — default to hidden to prevent flash.
  if (isOwnLoading) {
    return { hasAccess: false, isLoading: true, isOnBehalf };
  }

  // Entitlement present = access (non-metered, no quota check needed)
  return { hasAccess: !!entitlement, isLoading: false, isOnBehalf };
}
